import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { 
  Settings, History, Gift, Target, TrendingUp, DollarSign, 
  Users, Edit3, Save, X, ChevronDown, ChevronUp, Percent,
  CheckCircle
} from 'lucide-react';
import AnimatedCounter from '../../components/common/AnimatedCounter';
import { toast } from '../../components/common/Toast';
import { commissionRulesAPI, reportsAPI, getApiError } from '../../services/api';

const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-medium transition-all ${
      active ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
    {count !== undefined && <span className="ml-1 text-xs opacity-75">({count})</span>}
  </motion.button>
);

const RuleEditor = ({ rule, onSave, onCancel }) => {
  const { isDarkMode } = useTheme();
  const [form, setForm] = useState({ ...rule });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(rule.id, { commission_percentage: form.commission_percentage, threshold: form.threshold, max_payout: form.max_payout, enabled: form.enabled });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${isDarkMode ? 'bg-gray-800/90' : 'bg-white/90'} backdrop-blur-sm rounded-3xl border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} p-6 shadow-lg`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Editar Regla</h3>
        </div>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors">
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>
      <div className="space-y-4">
        {[
          { key: 'commission_percentage', label: 'Porcentaje de Comisión', icon: Percent, type: 'number', step: '0.1', suffix: '%' },
          { key: 'threshold', label: 'Umbral de Ventas ($)', icon: DollarSign, type: 'number', step: '1' },
          { key: 'max_payout', label: 'Pago Máximo ($)', icon: DollarSign, type: 'number', step: '1' },
        ].map(f => (
          <div key={f.key}>
            <label className={`block text-sm mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{f.label}</label>
            <div className="relative">
              <f.icon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type={f.type}
                step={f.step}
                value={form[f.key] ?? ''}
                onChange={(e) => setForm({ ...form, [f.key]: parseFloat(e.target.value) || 0 })}
                className={`w-full pl-10 pr-4 py-2.5 rounded-2xl border text-sm ${
                  isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                } focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all`}
              />
            </div>
          </div>
        ))}
        <div className="flex items-center gap-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="sr-only peer" />
            <div className={`w-10 h-5 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all ${form.enabled ? 'bg-orange-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
          </label>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{form.enabled ? 'Activado' : 'Desactivado'}</span>
        </div>
        <div className="flex gap-3 pt-2">
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={handleSave} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl text-sm font-medium shadow-lg shadow-orange-500/20 disabled:opacity-50"
          >{saving ? 'Guardando...' : <><Save className="w-4 h-4" /> Guardar</>}</motion.button>
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={onCancel}
            className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-2xl text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >Cancelar</motion.button>
        </div>
      </div>
    </div>
  );
};

const CommissionsAdmin = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [tab, setTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({ total_commission: 0, total_commission_paid: 0, total_commission_pending: 0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'rules') {
        const res = await commissionRulesAPI.getAll();
        setRules(res.data || []);
      } else if (tab === 'history') {
        const res = await reportsAPI.getAllCommissions();
        setHistory(res.data || []);
      }
    } catch (err) {
      toast.error(getApiError(err, 'Error al cargar datos'));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (history.length > 0) {
      setTotals({
        total_commission: history.reduce((s, h) => s + (h.amount || 0), 0),
        total_commission_paid: history.filter(h => h.status === 'paid' || h.is_processed).reduce((s, h) => s + (h.amount || 0), 0),
        total_commission_pending: history.filter(h => h.status !== 'paid' && !h.is_processed).reduce((s, h) => s + (h.amount || 0), 0),
      });
    }
  }, [history]);

  const handleSaveRule = async (id, data) => {
    try {
      await commissionRulesAPI.update(id, data);
      toast.success('Regla actualizada');
      setEditingRuleId(null);
      fetchData();
    } catch (err) {
      toast.error(getApiError(err, 'Error al guardar'));
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
        <h1 className={`text-3xl font-bold bg-gradient-to-r ${isDarkMode ? 'from-gray-100 to-gray-300' : 'from-gray-900 to-gray-600'} bg-clip-text text-transparent`}>
          {t('common.commissions')}
        </h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Configuración de comisiones e historial del sistema</p>
      </motion.div>

      <div className="flex gap-3 flex-wrap">
        <TabButton active={tab === 'rules'} onClick={() => setTab('rules')} icon={Settings} label="Reglas de Comisión" count={rules.length} />
        <TabButton active={tab === 'history'} onClick={() => setTab('history')} icon={History} label="Historial Global" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" />
        </div>
      ) : (
        <>
          {tab === 'rules' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {rules.map(rule => (
                <motion.div key={rule.id} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm hover:shadow-xl transition-all overflow-hidden`}
                >
                  {editingRuleId === rule.id ? (
                    <RuleEditor rule={rule} onSave={handleSaveRule} onCancel={() => setEditingRuleId(null)} />
                  ) : (
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${rule.enabled ? 'bg-gradient-to-br from-orange-500 to-orange-600' : 'bg-gray-400'} shadow-lg`}>
                            <Gift className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">{rule.name || `Regla #${rule.id}`}</h3>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{rule.description || (rule.enabled ? 'Activa' : 'Inactiva')}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1.5 rounded-xl text-xs font-medium ${
                          rule.enabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {rule.enabled ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 p-4 bg-orange-50/30 dark:bg-orange-900/10 rounded-2xl">
                        <div className="text-center">
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Comisión</p>
                          <p className="font-bold text-lg text-orange-600 dark:text-orange-400">{rule.commission_percentage ?? 0}%</p>
                        </div>
                        <div className="text-center">
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Umbral</p>
                          <p className="font-bold text-lg text-gray-900 dark:text-white">${(rule.threshold ?? 0).toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Máx. Pago</p>
                          <p className="font-bold text-lg text-gray-900 dark:text-white">${(rule.max_payout ?? 0).toLocaleString()}</p>
                        </div>
                      </div>
                      <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        onClick={() => setEditingRuleId(rule.id)}
                        className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2.5 border border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400 rounded-2xl text-sm font-medium hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                      >
                        <Edit3 className="w-4 h-4" /> Editar Regla
                      </motion.button>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {tab === 'history' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { label: 'Comisiones Totales', value: totals.total_commission, icon: DollarSign, color: 'from-blue-500 to-cyan-500' },
                  { label: 'Comisiones Pagadas', value: totals.total_commission_paid, icon: CheckCircle, color: 'from-green-500 to-emerald-500' },
                  { label: 'Comisiones Pendientes', value: totals.total_commission_pending, icon: TrendingUp, color: 'from-orange-500 to-red-500' },
                ].map((item, i) => (
                  <motion.div key={item.label} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }} whileHover={{ y: -4 }}
                    className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm hover:shadow-xl transition-all`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</p>
                        <p className={`text-3xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {typeof item.value === 'number' && !Number.isInteger(item.value) ? (
                            <>$<AnimatedCounter value={item.value} decimals={2} /></>
                          ) : (
                            <>$<AnimatedCounter value={item.value} /></>
                          )}
                        </p>
                      </div>
                      <div className={`p-3 rounded-2xl bg-gradient-to-r ${item.color} shadow-lg`}>
                        <item.icon className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm overflow-hidden`}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <th className="text-left px-6 py-4 font-medium">Usuario</th>
                        <th className="text-left px-6 py-4 font-medium">Tipo</th>
                        <th className="text-left px-6 py-4 font-medium">Monto</th>
                        <th className="text-left px-6 py-4 font-medium">Fecha</th>
                        <th className="text-left px-6 py-4 font-medium">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h, i) => (
                        <motion.tr key={h.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.03 }}
                          className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'} hover:bg-orange-50/30 dark:hover:bg-orange-900/10 transition-colors`}
                        >
                          <td className="px-6 py-4 font-medium">{h.user_name || `User #${h.user_id}`}</td>
                          <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{h.commission_type || h.type || 'Comisión'}</td>
                          <td className="px-6 py-4 font-semibold">${(h.amount || 0).toFixed(2)}</td>
                          <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{h.created_at ? new Date(h.created_at).toLocaleDateString() : ''}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                              h.status === 'paid' || h.is_processed ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                              h.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                              'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}>{h.status || (h.is_processed ? 'paid' : 'pending')}</span>
                          </td>
                        </motion.tr>
                      ))}
                      {history.length === 0 && (
                        <tr><td colSpan={5} className="text-center py-12 text-gray-400 dark:text-gray-500">No hay comisiones registradas</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </>
          )}
        </>
      )}
    </motion.div>
  );
};

export default CommissionsAdmin;
